import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle, Loader, X } from 'lucide-react';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { staffManagerApiService } from '../../../services/staffManagerApi';
import type { RemoveTeamResult, StaffTeam } from '../../../types/staffManager';
import { groupsLostOnRemoval } from '../../../utils/staffTenure';

interface RemoveFromTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: StaffTeam | null;
  member: { user_id: string; username: string } | null;
  /** All teams, for the blast-radius preview (groups still granted elsewhere are kept). */
  teams: StaffTeam[];
  onSuccess: (result: RemoveTeamResult, target: { userId: string; teamId: string }) => void;
}

/** Confirm removing a member from a team, previewing exactly which groups they will lose. */
export const RemoveFromTeamModal = ({ isOpen, onClose, team, member, teams, onSuccess }: RemoveFromTeamModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RemoveTeamResult | null>(null);
  useOnClickOutside(modalRef, onClose);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const lost = useMemo(() => (team && member ? groupsLostOnRemoval(teams, member.user_id, team.id) : []), [teams, team, member]);
  const kept = useMemo(() => (team ? team.groups.filter((g) => !lost.some((l) => l.id === g.id)) : []), [team, lost]);

  if (!isOpen || !team || !member) return null;

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await staffManagerApiService.removeFromTeam({ user_id: member.user_id, team_id: team.id });
      setSuccess(result);
      onSuccess(result, { userId: member.user_id, teamId: team.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div ref={modalRef} className="glass-card p-4 md:p-6 rounded-lg w-full max-w-md max-md:max-w-[95vw] relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-glass-400 hover:text-white touch-manipulation" aria-label="Close">
          <X />
        </button>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-4 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-green-200">{success.message}</div>
                <p className="text-xs text-green-100/80 mt-1">
                  {success.removed_groups.length > 0 ? (
                    <>
                      Groups removed: <span className="font-mono">{success.removed_groups.join(', ')}</span>
                    </>
                  ) : (
                    'No groups were removed.'
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="glass-button">
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-500/20 text-red-300 mb-4 mx-auto flex items-center justify-center rounded-full">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold mb-1">Remove from {team.display_name}?</h2>
              <p className="text-sm text-glass-400">
                <span className="font-semibold text-white">@{member.username}</span> will leave the team immediately. Tesseract records you as the actor.
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-glass-200/15 p-3 text-left space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-500">What changes</div>
              {lost.length > 0 ? (
                <p className="text-sm text-red-200">
                  Loses groups: <span className="font-mono">{lost.map((g) => g.name).join(', ')}</span>
                </p>
              ) : (
                <p className="text-sm text-glass-300">No access groups are lost.</p>
              )}
              {kept.length > 0 && (
                <p className="text-xs text-glass-400">
                  Kept (still granted by another team): <span className="font-mono">{kept.map((g) => g.name).join(', ')}</span>
                </p>
              )}
            </div>

            {error && (
              <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">{error}</div>
            )}

            <div className="mt-6 flex justify-end space-x-2">
              <button type="button" onClick={onClose} className="glass-button-secondary" disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirm()}
                className="glass-button bg-red-500/80 hover:bg-red-500/100 text-white text-on-solid flex items-center justify-center w-36"
                disabled={submitting}
              >
                {submitting ? <Loader className="w-5 h-5 animate-spin" /> : 'Remove'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
